// Glide extension for the shared slider: 1:1 dragging, rubber-band edges, one card per flick and velocity-matched settling.
window.SolferinoMomentumSwipe = function (glide, components, events) {
  const SLOP = 6; // px before the gesture direction is decided
  const SAMPLE_WINDOW = 100; // ms of pointer history used for the release velocity
  const PROJECTION = 220; // ms of travel projected from the release velocity, to read the swipe intent
  const FLICK_VELOCITY = 0.2; // px/ms: a flick always changes slide
  const COMMIT_RATIO = 0.2; // share of a slide past which a released drag changes slide
  const QUICK_SWIPE = { distance: 20, duration: 300 }; // px, ms: a short, quick swipe always changes slide
  const EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const EASING_SLOPE = 0.61 / 0.22; // initial speed of EASING, relative to linear
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let track;
  let gesture;
  let frame;

  function pitch() {
    return components.Sizes.slideWidth + components.Gaps.value;
  }

  function direction() {
    return components.Direction.is('rtl') ? -1 : 1;
  }

  // iOS-like resistance: the further past the edge, the less the slide follows.
  function rubberBand(overflow) {
    const size = components.Sizes.width || pitch();
    return Math.sign(overflow) * (1 - 1 / (Math.abs(overflow) * 0.55 / size + 1)) * size;
  }

  // Offset shown for a raw finger offset, relative to the current index (positive = towards previous slides).
  function resist(raw) {
    const max = glide.index * pitch();
    const min = -(components.Html.slides.length - 1 - glide.index) * pitch();
    if (raw > max) return max + rubberBand(raw - max);
    if (raw < min) return min + rubberBand(raw - min);
    return raw;
  }

  function render() {
    frame = null;
    if (!gesture || !gesture.horizontal) return;
    gesture.offset = resist(gesture.raw);
    components.Translate.set(components.Move.translate - gesture.offset * direction());
    events.emit('swipe.move');
  }

  // Samples include the press and the release, so a flick of only a few events still has a speed;
  // a finger held still before lifting leaves no movement in the window, hence no speed.
  function velocity(now) {
    const samples = gesture.samples.filter(function (sample) { return now - sample.t <= SAMPLE_WINDOW; });
    if (samples.length < 2) return 0;
    const first = samples[0];
    const last = samples[samples.length - 1];
    if (last.t === first.t) return 0;
    return Math.max(-3, Math.min(3, (last.x - first.x) / (last.t - first.t)));
  }

  // Settle with the same speed the finger had, then ease out.
  function settle(targetOffset, speed, action) {
    const distance = Math.abs(targetOffset - gesture.offset);
    const saved = { duration: glide.settings.animationDuration, easing: glide.settings.animationTimingFunc };
    let duration = speed > 0.05 ? EASING_SLOPE * distance / speed : 260 + distance * 0.3;
    duration = reducedMotion.matches ? 0 : Math.round(Math.max(200, Math.min(550, duration)));
    glide.settings.animationDuration = duration;
    glide.settings.animationTimingFunc = EASING;
    components.Transition.enable();
    action();
    glide.settings.animationDuration = saved.duration;
    glide.settings.animationTimingFunc = saved.easing;
  }

  function start(event) {
    const enabled = event.pointerType === 'mouse' ? glide.settings.dragThreshold : glide.settings.swipeThreshold;
    if (!enabled || glide.disabled || gesture || !event.isPrimary || event.button !== 0) return;
    if (event.target.closest('button, input, select, textarea, video')) return;
    const now = performance.now();
    gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, x0: event.clientX, t0: now, raw: 0, offset: 0, samples: [{ x: event.clientX, t: now }], horizontal: false };
  }

  function move(event) {
    if (!gesture || event.pointerId !== gesture.id) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    // performance.now(): event timestamps do not share one clock on every mobile browser.
    gesture.samples.push({ x: event.clientX, t: performance.now() });
    if (gesture.samples.length > 30) gesture.samples.splice(0, gesture.samples.length - 30);
    if (!gesture.horizontal) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SLOP) return;
      if (Math.abs(dy) >= Math.abs(dx)) { gesture = null; return; }
      // Start from where the finger is now, so the slide does not jump by the slop distance.
      gesture.horizontal = true;
      gesture.x = event.clientX;
      track.setPointerCapture(event.pointerId);
      components.Transition.disable();
      events.emit('swipe.start');
      components.Html.root.classList.add(glide.settings.classes.dragging);
    }
    event.preventDefault();
    gesture.raw = (event.clientX - gesture.x) * direction();
    if (!frame) frame = requestAnimationFrame(render);
  }

  // iOS Safari decides on the first touchmove whether the touch scrolls the page, before the slop is
  // reached: keep it from scrolling as soon as the finger moves more sideways than up or down.
  function blockNativePan(event) {
    if (!gesture || !event.cancelable) return;
    const touch = event.touches[0];
    const sideways = Math.abs(touch.clientX - gesture.x0) > Math.abs(touch.clientY - gesture.y);
    if (gesture.horizontal || sideways) event.preventDefault();
  }

  function end(event) {
    if (event.type === 'lostpointercapture' && event.target !== track) return;
    if (!gesture || event.pointerId !== gesture.id) return;
    if (!gesture.horizontal) { gesture = null; return; }
    if (frame) { cancelAnimationFrame(frame); frame = null; }
    if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    components.Html.root.classList.remove(glide.settings.classes.dragging);

    const size = pitch();
    const last = components.Html.slides.length - 1;
    // A cancel after the swipe was recognised (Safari taking over the touch) still counts as a release,
    // from the last known finger position: its own coordinates are not reliable.
    const now = performance.now();
    const x = event.type === 'pointerup' ? event.clientX : gesture.samples[gesture.samples.length - 1].x;
    if (event.type === 'pointerup') gesture.samples.push({ x: x, t: now });
    const v = velocity(now) * direction();
    const travel = (x - gesture.x0) * direction();
    const quick = Math.abs(travel) > QUICK_SWIPE.distance && now - gesture.t0 < QUICK_SWIPE.duration;
    gesture.raw = (x - gesture.x) * direction();
    gesture.offset = resist(gesture.raw);

    // Paging: a flick moves one card; more cards only when the finger actually dragged across them.
    const intent = gesture.offset + v * PROJECTION;
    let steps = -Math.round(gesture.offset / size);
    if (!steps && (Math.abs(intent) > size * COMMIT_RATIO || Math.abs(v) > FLICK_VELOCITY)) steps = (intent || v) > 0 ? -1 : 1;
    else if (!steps && quick) steps = travel > 0 ? -1 : 1;
    const target = Math.max(0, Math.min(last, glide.index + steps));
    const targetOffset = (glide.index - target) * size;
    // Only carry the finger's speed when it points to the destination.
    const speed = Math.sign(v) === Math.sign(targetOffset - gesture.offset) ? Math.abs(v) : 0;

    settle(targetOffset, speed, function () {
      if (target === glide.index) components.Move.make();
      else components.Run.make('=' + target);
    });
    gesture = null;
    events.emit('swipe.end');
  }

  function destroy() {
    if (frame) cancelAnimationFrame(frame);
    if (gesture && track.hasPointerCapture(gesture.id)) track.releasePointerCapture(gesture.id);
    gesture = null;
    frame = null;
    track.removeEventListener('pointerdown', start);
    track.removeEventListener('pointermove', move);
    track.removeEventListener('touchmove', blockNativePan);
    document.removeEventListener('pointerup', end);
    document.removeEventListener('pointercancel', end);
    track.removeEventListener('lostpointercapture', end);
    components.Html.root.classList.remove(glide.settings.classes.dragging);
  }
  events.on('destroy', destroy);
  return {
    mount: function () {
      track = components.Html.wrapper;
      track.addEventListener('pointerdown', start);
      track.addEventListener('pointermove', move);
      track.addEventListener('touchmove', blockNativePan, { passive: false });
      document.addEventListener('pointerup', end);
      document.addEventListener('pointercancel', end);
      track.addEventListener('lostpointercapture', end);
    }
  };
};
